import React, { useContext, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"
import ReactPlayer from "react-player"
import { T } from "../../design-system/styles/typography"
import Grid from "../shared/grid"
import FadedTitle from "../shared/faded-title"
import { UIContext } from "../shared/layout"
import VisibilitySensor from "react-visibility-sensor"
import Button from "../../design-system/atoms/button"
import {
  Title,
  FadedTitleWrapper,
  Orange,
  StyledFluid,
  titleImageStyle,
  locationDataStyle,
  VideoContainer,
  syncIntStyle,
  DetailsWrapper,
  DetailsBody,
  locationDetailsStyle,
  mapGeoDetailsStyle,
  syncIntDetailsStyle,
  videoProgressWrapperStyle,
} from "./location-data.style"
import Block from "../../design-system/atoms/layout-primitives/block"
import { css } from "styled-components"
import useVideoProgressManager from "../shared/hooks/useVideoProgressManager"
import _VideoIconProgressBar from "../../design-system/molecules/video-icon-progress-bar"

const VideoIconProgressBar = (props) => (
  <_VideoIconProgressBar
    {...props}
    titleVariants={{ sm: { color: "var(--tricorn-black)" } }}
    containerVariants={{ sm: { gridGap: "24px" } }}
  />
)

const LocationData = () => {
  const { state: UIState } = useContext(UIContext)
  const query = useStaticQuery(graphql`
    query {
      macSrc: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Map Editor/editor mac.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1440, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      locationDetailsSrc: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Map Editor/location details.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1440, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      syncIntegrationsSrc: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Map Editor/syncs_integrations.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1440, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      editorFade: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Map Editor/editor_fade_title.svg"
        }
      ) {
        publicURL
      }
      polyHighlightIcon: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Map Editor/control icons/highlight polygons.svg"
        }
      ) {
        publicURL
      }
      splitMergeIcon: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Map Editor/control icons/split merge.svg"
        }
      ) {
        publicURL
      }
      polyTraceIcon: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Map Editor/control icons/tracing.svg"
        }
      ) {
        publicURL
      }
    }
  `)

  const [isVideoVisible, setIsVideoVisible] = useState(false)

  const { videoPlayerRef, videoProgress, setVideoProgress, seekToTimestamp } =
    useVideoProgressManager()

  const VideoElementDetails = {
    polyTrace: {
      title: "Tracing Geometry",
      start: 0,
      end: 7,
      style: videoProgressWrapperStyle,
    },
    polyHighlight: {
      title: "Highlight Polygons",
      start: 7,
      end: 9,
      style: videoProgressWrapperStyle,
    },
    splitMerge: {
      title: "Split & Merge",
      start: 9,
      end: 20,
      style: videoProgressWrapperStyle,
    },
  }

  return (
    <Grid>
      <FadedTitleWrapper>
        <FadedTitle
          sm={T.sm.h2`400`}
          md={T.md.h2`400`}
          lg={T.lg.h2`400`}
          title={
            <>
              <Orange>Manage</Orange> Location Data
            </>
          }
          image={query.editorFade.publicURL}
          alt="Editor"
          fadeSettings={{ lg: "left" }}
          textAlign={UIState.breakpoint === "lg" ? "left" : "center"}
        />
      </FadedTitleWrapper>
      <StyledFluid
        styleModifier={titleImageStyle}
        imgStyle={{ objectFit: "contain" }}
        fluid={query.macSrc.childImageSharp.fluid}
        alt="A mac editor displaying the Mappedin Map Editor"
      />
      <DetailsWrapper styleModifier={locationDetailsStyle}>
        <Title>Location Details</Title>
        <DetailsBody>
          Edit location information including names, logos, and descriptions.
          Create customizable fields for relevant details such as hours, phone
          numbers, social media accounts, and more.
        </DetailsBody>
      </DetailsWrapper>
      <StyledFluid
        styleModifier={locationDataStyle}
        imgStyle={{ objectFit: "contain" }}
        fluid={query.locationDetailsSrc.childImageSharp.fluid}
        alt="An example of the shop details for Sport Check."
      />
      <DetailsWrapper styleModifier={mapGeoDetailsStyle}>
        <Title>Map Geometry</Title>
        <DetailsBody>
          As locations within your venue change, grow, or move around, ensure
          map accuracy with easy editing tools. Use real-world measurements for
          square footage, wall lengths, and path distances.
        </DetailsBody>
      </DetailsWrapper>
      <VideoContainer>
        <VisibilitySensor
          onChange={setIsVideoVisible}
          minTopValue={50}
          partialVisibility={true}
        >
          <ReactPlayer
            url="https://vimeo.com/427469692/95d781fd7f"
            playing={isVideoVisible}
            loop={true}
            volume={0}
            muted={true}
            controls={true}
            playsinline={true}
            onProgress={(data) => setVideoProgress(data)}
            ref={videoPlayerRef}
          />
        </VisibilitySensor>
      </VideoContainer>
      <Block
        variants={[
          css`
            && {
              display: grid;
            }
            grid-row: 7;
            grid-column: 2 / 12;
            grid-template-columns: 1fr;
            grid-column-gap: 114px;
            grid-row-gap: 24px;
          `,
          css`
            grid-row: 6;
            grid-column: 4 / -4;
          `,
          css`
            grid-row: 4;
            grid-column: 8 / 11;
            margin-top: 48px;
          `,
        ]}
      >
        <VideoIconProgressBar
          details={VideoElementDetails.polyTrace}
          videoProgress={videoProgress}
          seekMethod={seekToTimestamp}
          icon={<img src={query.polyTraceIcon.publicURL} />}
        />
        <VideoIconProgressBar
          details={VideoElementDetails.polyHighlight}
          videoProgress={videoProgress}
          seekMethod={seekToTimestamp}
          icon={<img src={query.polyHighlightIcon.publicURL} />}
        />
        <VideoIconProgressBar
          details={VideoElementDetails.splitMerge}
          videoProgress={videoProgress}
          seekMethod={seekToTimestamp}
          icon={<img src={query.splitMergeIcon.publicURL} />}
        />
      </Block>
      <StyledFluid
        styleModifier={syncIntStyle}
        imgStyle={{ objectFit: "contain" }}
        fluid={query.syncIntegrationsSrc.childImageSharp.fluid}
        alt="1. Your CMS Data, 2. Mappedin CMS, 3. Digital Solutions"
      />
      <DetailsWrapper styleModifier={syncIntDetailsStyle}>
        <Title>CMS Syncs & Integrations</Title>
        <DetailsBody>
          Have confidence in your data with a centralized platform. Use
          Mappedin’s Map Editor or an existing Content Management System that
          can showcase up-to-date venue data through imports and syncs.
        </DetailsBody>
      </DetailsWrapper>
      <Button.ScrollLink
        to="contact-form"
        smooth
        duration={1000}
        variants={{
          sm: {
            gridColumn: "2 / -2",
            justifySelf: "center",
            marginTop: "32px",
            marginBottom: "64px",
            gridRow: "10",
          },
          md: { gridColumn: "4 / -4", gridRow: "9" },
          lg: {
            gridColumn: "2 / 5",
            justifySelf: "left",
            marginTop: "0px",
            marginBottom: "96px",
            gridRow: "6",
            maxHeight: "46px",
          },
        }}
      >
        Book A Demo
      </Button.ScrollLink>
    </Grid>
  )
}

export default LocationData
